import React, { useEffect, useRef } from 'react';
import { CircularProgress, MenuItem, TextField } from '@material-ui/core';
import clsx from 'clsx';
import useStyles from 'app/cluster-styles/useStyles';
import useProviderData from '../hooks/useProviderData';
import { ZoneMenuItemContent } from './ZoneAvailabilityComponents';

/**
 * Zone dropdown.
 *
 * By default zones come from the provider catalog (providers.json). A caller
 * with live zones (the AWS form) passes `options`. `getOptionState(zone)` lets
 * the caller disable individual zones with an inline reason — used to make a
 * zone that does not offer the selected instance type unselectable instead
 * of letting the user find out at deploy time.
 */
export default function ZoneSelector({
    provider, regionId, value, onChange, className,
    options, loading = false, disabled = false, getOptionState, error = false, helperText,
}) {
    const classes = useStyles();
    const { zonesForRegion } = useProviderData(provider);
    const zones = options || zonesForRegion(regionId);
    const prevRegionRef = useRef(regionId);

    // Cascading clear: when region changes and current zone is no longer valid, auto-clear
    useEffect(() => {
        if (prevRegionRef.current === regionId) return;
        prevRegionRef.current = regionId;

        if (!value) return;
        const stillValid = zones.some((z) => z.zone_id === value);
        if (!stillValid) {
            onChange('');
        }
    }, [regionId, zones, value, onChange]);

    const noRegion = !regionId;
    const isDisabled = disabled || noRegion || loading || zones.length === 0;
    const safeValue = zones.some((z) => z.zone_id === value) ? value : '';
    const label = noRegion ? 'Select a Region first' : (loading ? 'Loading zones...' : 'Zone');

    return (
        <TextField
            select
            className={clsx(classes.input, className || "flex-1")}
            id="rm-zone"
            name="rm_zone_id"
            label={label}
            variant="outlined"
            value={safeValue}
            onChange={(e) => onChange(e.target.value)}
            required={!isDisabled}
            disabled={isDisabled}
            error={error}
            helperText={helperText}
            SelectProps={{
                // The closed field shows just the zone name; the inline
                // "Not offered" reason belongs to the open list only.
                renderValue: (selected) => {
                    const zone = zones.find((z) => z.zone_id === selected);
                    return zone ? zone.name : selected;
                },
                MenuProps: {
                    PaperProps: {
                        style: { maxHeight: 300 },
                    },
                },
            }}
        >
            {loading && (
                <MenuItem disabled>
                    <CircularProgress size={16} className="mr-8" />
                    Loading zones...
                </MenuItem>
            )}
            {zones.map((zone) => {
                const state = getOptionState ? getOptionState(zone) : null;
                const rowDisabled = Boolean(state && state.disabled);
                return (
                    // Full strength: ZoneMenuItemContent fades the name and keeps the reason readable.
                    <MenuItem key={zone.zone_id} value={zone.zone_id} disabled={rowDisabled} style={rowDisabled ? { opacity: 1 } : undefined}>
                        <ZoneMenuItemContent zoneName={zone.name} zoneId={zone.zone_id}
                            disabled={rowDisabled} reason={state ? state.reason : null} />
                    </MenuItem>
                );
            })}
        </TextField>
    );
}
